import React, { useEffect, useState } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { ssgData } from "@/lib/ssg";

export const StoryCarousel = ({ category, eyebrow, heading, note }) => {
  const [items, setItems] = useState(() => ssgData(`testimonials:${category}`) || []);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    api.get(`/testimonials?category=${category}`).then(({ data }) => setItems(data)).catch(() => {});
  }, [category]);

  useEffect(() => {
    if (paused || items.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % items.length), 8000);
    return () => clearInterval(id);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const s = items[idx];

  return (
    <div data-testid={`story-carousel-${category}`}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div className="max-w-2xl">
          <p className="eyebrow text-[#A39171] mb-4">{eyebrow}</p>
          <h2 className="font-display text-4xl md:text-5xl text-[#2D2623] tracking-tight leading-tight">
            {heading}
          </h2>
          {note && <p className="mt-5 text-[#5C524E] leading-relaxed max-w-xl">{note}</p>}
        </div>
        {items.length > 1 && (
          <div className="flex items-center gap-3">
            <button
              data-testid="story-prev"
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              aria-label="Previous story"
              className="w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#A39171]/25 flex items-center justify-center text-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              data-testid="story-next"
              onClick={() => setIdx((i) => (i + 1) % items.length)}
              aria-label="Next story"
              className="w-12 h-12 rounded-full bg-[#FDFBF7] border border-[#A39171]/25 flex items-center justify-center text-[#2D2623] hover:bg-[#2D2623] hover:text-[#FDFBF7] transition-all"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <article
        key={s.id}
        data-testid={`story-${idx}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="fade-up bg-[#FDFBF7] border border-[#A39171]/15 rounded-3xl p-8 md:p-12 shadow-[0_20px_60px_rgba(163,145,113,0.10)]"
      >
        <div className="flex items-center justify-between mb-6">
          <Quote className="w-9 h-9 text-[#E07A5F]" />
          <div className="flex items-center gap-0.5 text-[#E2C044]">
            {[...Array(s.rating || 5)].map((_, j) => (
              <Star key={j} className="w-4 h-4 fill-current" />
            ))}
          </div>
        </div>
        <p className="font-display text-xl md:text-2xl text-[#2D2623] leading-relaxed">{s.quote}</p>
        <div className="mt-9 pt-6 border-t border-[#A39171]/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-medium text-[#2D2623]">{s.name}</p>
            <p className="text-sm text-[#5C524E] mt-0.5">{s.role} · {s.location}</p>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A39171]">
            {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </p>
        </div>
      </article>

      <div className="flex items-center justify-center gap-2 mt-8" data-testid="story-dots">
        {items.map((it, i) => (
          <button
            key={it.id}
            onClick={() => setIdx(i)}
            aria-label={`Go to story ${i + 1}`}
            data-testid={`story-dot-${i}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === idx ? "w-10 bg-[#E07A5F]" : "w-2 bg-[#A39171]/30 hover:bg-[#A39171]/60"
            }`}
          />
        ))}
      </div>
      <p className="mt-6 text-xs text-[#A39171] text-center max-w-2xl mx-auto">
        Shared with permission. All names, initials and identifying details have been changed or
        removed to protect patient privacy.
      </p>
    </div>
  );
};
